import type { InitiativeWriteInput } from '@complan/contracts';

/**
 * The controlled-value option data the intake form needs, and the default form
 * values.
 *
 * ## Active-only, with an inactive marker for referenced values (R5.9)
 *
 * Every controlled-value field offers **active** master data only. The one
 * exception R5.9 carves out: an inactive value the record under edit already
 * references stays selectable, shown with an inactive marker, so an existing
 * Initiative can be re-saved without silently dropping a value that was
 * deactivated after it was assigned. {@link toControlledOptions} builds that list
 * — active records, plus any already-referenced inactive record marked
 * `isInactive` — from the master data list and the current selection.
 */

/** One selectable controlled value. */
export interface ControlledOption {
  readonly id: string;
  /** Display label; already resolved, not a catalogue key (master data names are user data). */
  readonly label: string;
  /**
   * `true` when the value is inactive but retained because the record under edit
   * references it (R5.9). The field renders an inactive marker beside it.
   */
  readonly isInactive: boolean;
  /** Platform only: the configured colour (`#rrggbb`, R16.1), shown as a swatch. */
  readonly colorHex?: string;
}

/** A master data record as far as the option builder cares. */
export interface MasterOptionInput {
  readonly id: string;
  readonly name: string;
  readonly isActive: boolean;
  /** Platform only: the configured colour, `null` when unset. */
  readonly colour?: string | null;
}

/** The option's colour key, present only when the record has a colour. */
function colourOf(record: MasterOptionInput): { colorHex?: string } {
  return record.colour ? { colorHex: record.colour } : {};
}

/**
 * Build the option list for a controlled-value field (R5.9): every active record,
 * plus any inactive record whose id appears in `referencedIds`, the latter marked
 * inactive. Active records precede retained-inactive ones so the live choices
 * lead the list.
 */
export function toControlledOptions(
  records: readonly MasterOptionInput[],
  referencedIds: readonly string[],
): readonly ControlledOption[] {
  const referenced = new Set(referencedIds);
  const active = records
    .filter((record) => record.isActive)
    .map((record) => ({ id: record.id, label: record.name, isInactive: false, ...colourOf(record) }));
  const retainedInactive = records
    .filter((record) => !record.isActive && referenced.has(record.id))
    .map((record) => ({ id: record.id, label: record.name, isInactive: true, ...colourOf(record) }));
  return [...active, ...retainedInactive];
}

/**
 * The master data option sets the form reads. Supplied by the route (which will
 * load them from the admin master-data endpoints); the form itself is agnostic to
 * the fetch. Owners are included as a controlled set even though Foundation
 * exposes no dedicated user-list endpoint — see the route note.
 */
export interface IntakeOptionSets {
  readonly owners: readonly ControlledOption[];
  readonly platforms: readonly ControlledOption[];
  readonly channels: readonly ControlledOption[];
  readonly audiences: readonly ControlledOption[];
  readonly customerGroups: readonly ControlledOption[];
  readonly categories: readonly ControlledOption[];
  readonly initiativeTypes: readonly ControlledOption[];
  /** CSC attribute classifications (Inbound, Outbound, ...) for the CSC dependency block. */
  readonly cscAttributes: readonly ControlledOption[];
  readonly priorities: readonly ControlledOption[];
  readonly milestoneTypes: readonly ControlledOption[];
  readonly fieldForceActivityTypes: readonly ControlledOption[];
  /** Store-type / POS-segment options for the Field Force step (UI spec §6.2). */
  readonly storeTypes: readonly StoreTypeOption[];
  /**
   * The Region a Field Force driver's demand belongs to (Manpower R1.1, R4.6).
   *
   * Needed on the intake form because the Capacity & Utilisation calculation is
   * scoped per Region: a driver with no Region recorded contributes to no Region's
   * utilisation figure.
   */
  readonly regions: readonly ControlledOption[];
  /** The Population_Unit a driver's Impacted_Population_Count is counted in (R4.1). */
  readonly populationUnits: readonly ControlledOption[];
}

/**
 * A Field Force store-type / segment option carrying its POS count, from which
 * the derived POS count is summed (UI spec §6.2). The count is display input
 * only; Foundation stores the selection and the derived/override values, not the
 * manpower calculation.
 */
export interface StoreTypeOption {
  readonly id: string;
  readonly label: string;
  readonly posCount: number;
  readonly isInactive: boolean;
}

/** An empty option set, useful as a default and in tests. */
export const EMPTY_OPTION_SETS: IntakeOptionSets = {
  owners: [],
  platforms: [],
  channels: [],
  audiences: [],
  customerGroups: [],
  categories: [],
  initiativeTypes: [],
  cscAttributes: [],
  priorities: [],
  milestoneTypes: [],
  fieldForceActivityTypes: [],
  storeTypes: [],
  regions: [],
  populationUnits: [],
};

/**
 * A blank intake payload — the RHF default values for a new Initiative.
 *
 * Every collection starts empty and every involvement flag starts `false`, so a
 * new form shows no conditional section until the user opts in — which is exactly
 * what the visibility map reports for all-false flags.
 */
export function emptyIntakeValues(): InitiativeWriteInput {
  return {
    title: '',
    details: null,
    ownerUserId: '',
    backupOwnerUserId: null,
    startDate: '' as InitiativeWriteInput['startDate'],
    endDate: '' as InitiativeWriteInput['endDate'],
    platforms: [],
    channelIds: [],
    customerGroupIds: [],
    audienceIds: [],
    categoryIds: [],
    initiativeTypeIds: [],
    cscAttributeIds: [],
    salesCycleAssignment: { source: 'derived', salesCycleIds: [] },
    priorityId: '',
    isConfidential: false,
    confidentialityReason: null,
    fieldForceInvolvement: false,
    dteInvolvement: false,
    cscInvolvement: false,
    milestones: [],
    dependencies: [],
    fieldForceScope: [],
    referenceLinks: [],
  };
}
